import { useEffect, useState } from 'react'
import { fetchAppSubscription } from '@/lib/app-data/subscriptions'
import { appLog } from '@/lib/logger'
import { useAuth } from './auth-context'

export type SubscriptionStatus =
  | 'loading'
  | 'none'
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'

export interface SubscriptionInfo {
  status: SubscriptionStatus
  stripeCustomerId: string | null
  /** ISO date string when subscription/trial ends; null if not set. */
  currentPeriodEnd: string | null
}

const RETRY_DELAY_MS = 2000
const MAX_ATTEMPTS = 4

/** Pure: derive subscription status from DB row. Exported for unit tests. */
export function getStatusFromRow(
  data: { status: string; current_period_end: string | null } | null
): SubscriptionStatus {
  if (!data) return 'none'
  const expired =
    data.current_period_end != null &&
    new Date(data.current_period_end) < new Date()
  return expired ? 'canceled' : (data.status as SubscriptionStatus)
}

type SubRow = {
  status: string
  current_period_end: string | null
  stripe_customer_id: string | null
}

function rowToInfo(data: SubRow | null): SubscriptionInfo {
  return {
    status: getStatusFromRow(data),
    stripeCustomerId: data?.stripe_customer_id ?? null,
    currentPeriodEnd: data?.current_period_end ?? null,
  }
}

/**
 * Returns full subscription info including stripeCustomerId.
 * Use this when you need to determine subscription type (Stripe vs voucher).
 */
export function useSubscriptionInfo(): SubscriptionInfo {
  const { user } = useAuth()
  const [info, setInfo] = useState<SubscriptionInfo>({
    status: 'loading',
    stripeCustomerId: null,
    currentPeriodEnd: null,
  })

  useEffect(() => {
    if (!user) {
      queueMicrotask(() =>
        setInfo({ status: 'none', stripeCustomerId: null, currentPeriodEnd: null })
      )
      return
    }

    const fetchSub = async (): Promise<SubRow | null> => {
      try {
        const row = await fetchAppSubscription()
        if (!row || !row.status) return null
        return {
          status: row.status,
          current_period_end: row.current_period_end,
          stripe_customer_id: row.stripe_customer_id,
        }
      } catch (err) {
        appLog.warn('useSubscription app API error', {
          error: String(err),
          userId: user.id ? 'present' : null,
        })
        return null
      }
    }

    let cancelled = false
    let timeoutId: ReturnType<typeof setTimeout> | undefined

    queueMicrotask(() => {
      if (!cancelled)
        setInfo({ status: 'loading', stripeCustomerId: null, currentPeriodEnd: null })
    })

    const tryOnce = (attempt: number) => {
      if (cancelled) return
      appLog.debug('useSubscription fetch attempt', {
        attempt: attempt + 1,
        maxAttempts: MAX_ATTEMPTS,
      })
      fetchSub()
        .then((data) => {
          if (cancelled) return
          if (data) {
            const next = rowToInfo(data)
            const isExpired =
              data.current_period_end != null &&
              new Date(data.current_period_end) < new Date()
            appLog.debug('useSubscription row received', {
              attempt: attempt + 1,
              raw_status: data.status,
              derived_status: next.status,
              isExpired,
            })
            setInfo(next)
            return
          }
          if (attempt < MAX_ATTEMPTS - 1) {
            appLog.debug('useSubscription no row, retrying', {
              attempt: attempt + 1,
            })
            timeoutId = setTimeout(() => tryOnce(attempt + 1), RETRY_DELAY_MS)
          } else {
            appLog.warn('useSubscription no row after retries', {
              maxAttempts: MAX_ATTEMPTS,
            })
            setInfo({ status: 'none', stripeCustomerId: null, currentPeriodEnd: null })
          }
        })
        .catch((err) => {
          if (cancelled) return
          appLog.error('useSubscription fetch failed', {
            attempt: attempt + 1,
            error: String(err),
          })
          if (attempt < MAX_ATTEMPTS - 1) {
            timeoutId = setTimeout(() => tryOnce(attempt + 1), RETRY_DELAY_MS)
          } else {
            setInfo({ status: 'none', stripeCustomerId: null, currentPeriodEnd: null })
          }
        })
    }

    tryOnce(0)

    // Poll every 30s while mounted (covers Stripe webhook delay).
    const pollId = setInterval(() => {
      if (cancelled) return
      fetchSub()
        .then((data) => {
          if (cancelled || !data) return
          setInfo(rowToInfo(data))
        })
        .catch(() => {
          /* swallow; next tick retries */
        })
    }, 30_000)

    return () => {
      cancelled = true
      if (timeoutId) clearTimeout(timeoutId)
      clearInterval(pollId)
    }
  }, [user])

  return info
}

/**
 * Backwards-compatible hook that returns only the subscription status string.
 * Prefer useSubscriptionInfo() when you also need stripeCustomerId.
 */
export function useSubscription(): SubscriptionStatus {
  return useSubscriptionInfo().status
}

/** Returns true when the user has an active or trialing subscription. */
export function useIsSubscribed() {
  const status = useSubscription()
  return status === 'active' || status === 'trialing'
}
